/**
 * The selection bar above and below the Estimates table (#1349).
 *
 * Built to `threads/bulk-estimate-email/mockup.html` (approved 2026-08-31).
 *
 * Two pieces, because the table can be long enough that the card header
 * scrolls off: a select-all strip that sits above the rows, and a sticky bar
 * that follows the selection down the page. Both open the same dialog.
 *
 * Neither one holds the selection — the page does, so it survives a refetch
 * and a re-sort, exactly as the invoice batch bar works.
 */
import { Mail, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { formatDollars } from "@/lib/utils";
import { selectAllLabel, selectionCountLabel, validateSelection } from "./estimate-batch-copy";

/**
 * Above the table: check everything sendable that is on screen, and say
 * plainly how many rows are out of reach and why.
 */
export function EstimateSelectAllBar({
    sendableCount,
    blockedCount,
    selectedCount,
    allSelected,
    onSelectAll,
    onClear,
}: {
    sendableCount: number;
    blockedCount: number;
    selectedCount: number;
    allSelected: boolean;
    onSelectAll: (checked: boolean) => void;
    onClear: () => void;
}) {
    return (
        <div
            className="mb-2 flex flex-wrap items-center gap-2.5 rounded-md border bg-muted/50 px-3 py-2"
            data-testid="estimates-select-bar"
        >
            <Checkbox
                checked={allSelected && sendableCount > 0}
                aria-label="Select every sendable estimate shown"
                onCheckedChange={(v) => onSelectAll(v === true)}
            />
            <span className="text-xs font-medium">{selectAllLabel(sendableCount)}</span>
            <span className="flex-1 text-xs text-muted-foreground">
                {selectionCountLabel(selectedCount, blockedCount)}
            </span>
            {selectedCount > 0 && (
                <Button size="sm" variant="ghost" onClick={onClear}>
                    Clear
                </Button>
            )}
        </div>
    );
}

/**
 * Below the table, sticky: what is picked, what it is worth, and the way out.
 * Renders nothing at all when nothing is selected.
 */
export function EstimateBatchBar({
    selectedCount,
    selectedTotal,
    scopeLabel,
    onClear,
    onEmail,
}: {
    selectedCount: number;
    /** Summed from the rows on screen — the server totals it again for the dialog. */
    selectedTotal: number;
    /** What the selection actually covers, in words. Never implied. */
    scopeLabel: string;
    onClear: () => void;
    onEmail: () => void;
}) {
    if (selectedCount === 0) return null;
    const err = validateSelection(selectedCount);
    return (
        <div
            className="sticky bottom-4 z-20 mt-3 rounded-xl border border-primary/40 bg-card shadow-[var(--shadow-elevated)]"
            data-testid="estimates-batch-bar"
        >
            <div className="flex flex-wrap items-center gap-3 p-3">
                <span className="text-sm font-semibold">
                    {selectedCount} estimate{selectedCount === 1 ? "" : "s"} selected
                </span>
                <span className="min-w-[16rem] flex-1 text-xs text-foreground/70">{err ?? scopeLabel}</span>
                <span className="text-base font-bold tabular-nums">{formatDollars(selectedTotal)}</span>
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={onClear}>
                        <X className="mr-1 h-3.5 w-3.5" /> Clear
                    </Button>
                    <Button size="sm" disabled={!!err} onClick={onEmail}>
                        <Mail className="mr-1 h-4 w-4" /> Email {selectedCount} estimate
                        {selectedCount === 1 ? "" : "s"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
